import { HttpErrorResponse } from '@angular/common/http';
import { effect, inject, Injectable, signal } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { sift as radashSift } from 'radash';
import { firstValueFrom } from 'rxjs';
import { validate as validateUuid, version as versionUuid } from 'uuid';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import {
    EditEvent,
    EditEventAction,
    EntityProperties,
    StoredEvent,
} from '@connect-services/client/models/edit-event.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { EditModeStorageSaturationModalService } from '@connect-services/client/services/edit-mode-storage-saturation-modal.service';
import { EditSessionLogBuilderService } from '@connect-services/client/services/edition/edit-session-log-builder.service';
import { DeletionCandidate } from '@connect-services/client/services/map-edit-mode.service';
import { EditAction, EntityState, LayersService } from '@connect-services/src/sdk/api';

export type EditState = {
    [layerId: string]: LayerSnapshot;
};

export type LayerSnapshot = {
    [entityId: string]: EntityEdit;
};

export type EntityEdit = {
    action: EditAction;
    beforeProperties?: EntityProperties;
    businessIdentifier: { key: string; value: unknown };
    properties: EntityProperties;
    saveOrder?: number;
    tableName: string;
};

export type EditStorageUsage = {
    estimatedQuotaBytes: number;
    isQuotaExceeded: boolean;
    percentUsed: number;
    usedBytes: number;
};

export enum StorageSaturationDecision {
    CLEAR_HISTORY = 'clear_history',
    NONE = 'none',
    SAVE_REQUIRED = 'save_required',
}

type StorageSaturationResult = {
    decision: StorageSaturationDecision;
    storageUsage: EditStorageUsage;
};
type StorageSaturationContext = {
    newEventsJson: string;
    nextEventsJson: string;
    nextStateJson: string;
    projectId: string;
    updatedState: EditState;
};
type EditModeStoragePersistenceContext = {
    eventsStorageKey: string;
    nextEventsJson: string;
    nextStateJson: string;
    stateStorageKey: string;
};

@Injectable({
    providedIn: 'root',
})
export class EditModeStateService {
    private static readonly defaultEstimatedLocalStorageQuotaBytes = 5 * 1024 * 1024;

    public readonly canCancel = signal<boolean>(false);
    public readonly canReapply = signal<boolean>(false);
    public readonly pendingEditCount = signal<number>(0);
    public readonly pendingEditsByLayer = signal<Record<string, boolean>>({});

    protected readonly layersService = inject(LayersService); // Used in child service too

    private readonly boundOnStorageChange = this.onStorageChange.bind(this);
    private readonly editModeStorageSaturationModalService = inject(EditModeStorageSaturationModalService);
    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly editSessionLogBuilderService = inject(EditSessionLogBuilderService);
    private readonly pendingEditCountByLayer = signal<Record<string, number>>({});
    private readonly projectBloc: ProjectBloc = inject(ProjectBloc);
    private readonly stateTick = signal<number>(0);

    public constructor() {
        effect(() => {
            const projectId = this.projectBloc.value()?.id;

            if (!projectId) {
                return;
            }

            this.refreshPendingEditsState(projectId);
            this.initCanCancelAndReapply(projectId);
        });

        // only react on update of storage in another page than the current one.
        window.addEventListener('storage', this.boundOnStorageChange);
    }

    public addEdit(projectId: string, editEvent: EditEvent): void {
        this.addEdits(projectId, [editEvent]);
    }

    public addEdits(projectId: string, editEvents: EditEvent[]): void {
        this.addEditsInternal(projectId, editEvents);
    }

    public buildBefore(projectId: string, entityId: string): Record<string, unknown> {
        return this.getBeforePropertiesFromHistory(projectId, entityId) ?? {};
    }

    public cancelLastEdit(projectId: string): StoredEvent | null {
        const events = this.getEvents(projectId);
        const state = this.getState(projectId);
        let eventToCancel: StoredEvent | undefined;
        for (let index = events.length - 1; index >= 0; index--) {
            const event = events[index];
            if (event.canceled) {
                continue;
            }

            eventToCancel = event;
            break;
        }

        if (!eventToCancel) {
            return null;
        }

        eventToCancel.canceled = true;
        localStorage.setItem(this.getEditLocalStorageKey(projectId, 'events'), JSON.stringify(events));
        this.canReapply.set(true);
        if (events.every((event: StoredEvent) => event.canceled)) {
            this.canCancel.set(false);
        }

        this.updateStateOnCancel(projectId, state, events, eventToCancel);
        this.recomputePendingEditCount(projectId);
        this.notifyStateChanged();

        return eventToCancel;
    }

    public clearEditModeHistory(projectId: string): void {
        const state = this.getState(projectId);
        const compactedState = this.mergeStateWithBeforeProperties(projectId, state);

        localStorage.setItem(this.getEditLocalStorageKey(projectId, 'state'), JSON.stringify(compactedState));
        localStorage.removeItem(this.getEditLocalStorageKey(projectId, 'events'));

        void this.updateEditSessionLogState(projectId, compactedState);

        this.recomputePendingEditCount(projectId);
        this.canCancel.set(false);
        this.canReapply.set(false);
        this.notifyStateChanged();
    }

    public clearEditModeState(projectId: string): void {
        localStorage.removeItem(this.getEditLocalStorageKey(projectId, 'events'));
        localStorage.removeItem(this.getEditLocalStorageKey(projectId, 'state'));

        void this.clearEditSessionLogState();

        this.pendingEditCount.set(0);
        this.pendingEditsByLayer.set({});
        this.notifyStateChanged();
        this.canCancel.set(false);
        this.canReapply.set(false);
    }

    public async clearLayerStateAndEvents(projectId: string, layerIds: string[]): Promise<void> {
        for (const layerId of layerIds) {
            this.clearLayerEvents(projectId, layerId);
            this.clearLayerState(projectId, layerId);
        }

        try {
            const tableNames = await Promise.all(
                layerIds.map(async (layerId: string) => {
                    // LayerId can be a tableName when not stored in the project layers
                    if (validateUuid(layerId) && versionUuid(layerId) === 4) {
                        const layer = await firstValueFrom(this.layersService.layerControllerGetOne({ id: layerId }));

                        return layer.tableName;
                    }

                    return Promise.resolve(layerId);
                }),
            );

            await this.clearEditSessionLogState(radashSift(tableNames));
        } catch (error) {
            if (!(error instanceof HttpErrorResponse)) {
                Sentry.captureException(error);
            }
        }

        this.refreshPendingEditsState(projectId);
        this.initCanCancelAndReapply(projectId);
    }

    public createStorageUsage(
        estimatedQuotaBytes: number = EditModeStateService.defaultEstimatedLocalStorageQuotaBytes,
        usedBytes: number = this.getUsedStorageBytes(),
    ): EditStorageUsage {
        const percentUsed = Math.min((usedBytes / estimatedQuotaBytes) * 100, 100);

        return {
            estimatedQuotaBytes,
            isQuotaExceeded: usedBytes >= estimatedQuotaBytes,
            percentUsed,
            usedBytes,
        };
    }

    public getBulkDelete(params: { candidates: DeletionCandidate[]; projectId: string }): EditEventAction[] {
        const { candidates, projectId } = params;

        if (!candidates.length) {
            return [];
        }

        return candidates.map((candidate: DeletionCandidate) => {
            const before = this.buildDeleteBeforeSnapshot(projectId, candidate);

            return {
                action: EditAction.DELETE,
                after: {},
                before,
                businessIdentifier: { key: 'uuid', value: candidate.entityId },
                entityId: candidate.entityId,
                layerId: candidate.layerId,
                tableName: candidate.tableName,
            };
        });
    }

    public getEditedProperty(
        projectId: string,
        layerId: string,
        entityId: string,
        attributeKey: string,
    ): unknown | undefined {
        const entityState = this.getEntityState(projectId, layerId, entityId);

        if (!entityState?.properties) {
            return undefined;
        }

        const hasAttribute = Object.prototype.hasOwnProperty.call(entityState.properties, attributeKey);
        if (!hasAttribute) {
            return undefined;
        }

        return entityState.properties[attributeKey];
    }

    public getEntityState(projectId: string, layerId: string, entityId: string): EntityState | null {
        const snapshot: EditState = this.getState(projectId);
        const entity = snapshot[layerId]?.[entityId];

        if (!entity) {
            return null;
        }

        return this.formatEntityState(entityId, entity);
    }

    public getLayerState(projectId: string, layerId: string): EntityState[] {
        const snapshot: EditState = this.getState(projectId);

        return Object.entries(snapshot[layerId] ?? {}).map(([entityId, entity]: [string, EntityEdit]): EntityState =>
            this.formatEntityState(entityId, entity),
        );
    }

    public getPendingEditCount(projectId: string): number {
        const state = this.getState(projectId);

        return Object.values(state).reduce((acc, layerSnapshot) => {
            if (!layerSnapshot) {
                return acc;
            }

            return acc + Object.keys(layerSnapshot).length;
        }, 0);
    }

    public getPendingEditCountForLayers(layerIds: string[]): number {
        if (!layerIds?.length) {
            return 0;
        }

        const byLayer = this.pendingEditCountByLayer();

        return layerIds.reduce((sum, id) => sum + (byLayer[id] ?? 0), 0);
    }

    public getProjectState(layerIds?: string[]): EditState {
        const projectId = this.projectBloc.value()?.id;
        AssertService.isString(projectId);
        const projectState = this.getState(projectId);

        if (!layerIds?.length) {
            return projectState;
        }

        return layerIds.reduce((acc: EditState, layerId: string) => {
            const layerState = projectState[layerId];

            if (layerState && Object.keys(layerState).length > 0) {
                acc[layerId] = layerState;
            }

            return acc;
        }, {});
    }

    public getState(projectId: string): EditState {
        const localStorageKey = this.getEditLocalStorageKey(projectId, 'state');
        const stateJson = localStorage.getItem(localStorageKey);

        return !stateJson ? {} : JSON.parse(stateJson!);
    }

    public getStateTick(): number {
        return this.stateTick();
    }

    public hasPendingDeleteEdits(layerIds?: string[]): boolean {
        const projectState = this.getProjectState(layerIds);

        return Object.values(projectState).some((layerSnapshot: LayerSnapshot) =>
            Object.values(layerSnapshot).some((entityEdit: EntityEdit) => entityEdit.action === EditAction.DELETE),
        );
    }

    public hasPendingEditsForLayer(layerId: string): boolean {
        return this.pendingEditsByLayer()[layerId];
    }

    public isCreatedEntity(layerId: string, entityId: string): boolean {
        const projectId: string | undefined = this.projectBloc.value()?.id;
        AssertService.isString(projectId);

        return this.getEntityState(projectId, layerId, entityId)?.action === EditAction.CREATE;
    }

    public notifyEditStateChanged(projectId: string): void {
        this.refreshPendingEditsState(projectId);
    }

    public reapplyLastCanceledEdit(projectId: string): StoredEvent | null {
        const events = this.getEvents(projectId);

        const firstCanceled = events.find((event: StoredEvent) => event.canceled);

        if (!firstCanceled) {
            return null;
        }

        firstCanceled.canceled = false;
        const editLocalStorageKey = this.getEditLocalStorageKey(projectId, 'events');
        localStorage.setItem(editLocalStorageKey, JSON.stringify(events));
        this.updateState(projectId, firstCanceled);

        this.canCancel.set(true);

        this.recomputePendingEditCount(projectId);
        this.notifyStateChanged();

        if (events.some((event: StoredEvent): boolean => event.canceled)) {
            return firstCanceled;
        }

        this.canReapply.set(false);

        return firstCanceled;
    }

    protected clearEditSessionLogState(tableNames?: string[]): Promise<void> {
        return this.editSessionLogBuilderService.endSession(tableNames);
    }

    protected getEditLocalStorageKey(projectId: string, action: 'events' | 'state' | ''): string {
        return `${this.getEditLocalStoragePrefix()}${projectId}/${action}`;
    }

    protected getEditLocalStoragePrefix(): string {
        return 'editMode/';
    }

    protected updateEditSessionLogState(projectId: string, state: EditState): Promise<void> {
        return this.editSessionLogBuilderService.createEditSessionLogs(projectId, state);
    }

    private addEditsInternal(projectId: string, editEvents: EditEvent[]): void {
        if (!editEvents.length) {
            return;
        }

        const existingEvents = this.getEvents(projectId);

        const newEvents: StoredEvent[] = editEvents.map((event: EditEvent) => {
            return { ...event, canceled: false };
        });

        const existingNotCanceledEvents = existingEvents.filter((event: StoredEvent) => !event.canceled);
        const nextEvents = [...existingNotCanceledEvents, ...newEvents];

        const currentState = this.getState(projectId);
        const updatedState: EditState = editEvents.reduce((finalState: EditState, event: EditEvent) => {
            return this.applyEditEventToState(projectId, event, finalState);
        }, currentState);

        const newEventsJson = JSON.stringify(newEvents);
        const nextEventsJson = JSON.stringify(nextEvents);
        const nextStateJson = JSON.stringify(updatedState);
        const storageSaturationResult = this.buildStorageSaturationDecision({
            newEventsJson,
            nextEventsJson,
            nextStateJson,
            projectId,
            updatedState,
        });

        if (storageSaturationResult.decision !== StorageSaturationDecision.NONE) {
            this.openStorageSaturationModal(projectId, editEvents, storageSaturationResult);

            return;
        }

        const eventsStorageKey = this.getEditLocalStorageKey(projectId, 'events');
        const stateStorageKey = this.getEditLocalStorageKey(projectId, 'state');
        const isPersisted = this.persistEditModeStorage({
            eventsStorageKey,
            nextEventsJson,
            nextStateJson,
            stateStorageKey,
        });

        if (!isPersisted) {
            this.openStorageSaturationModal(projectId, editEvents, {
                decision: StorageSaturationDecision.SAVE_REQUIRED,
                storageUsage: this.createStorageUsage(),
            });

            return;
        }

        void this.updateEditSessionLogState(projectId, updatedState);

        this.recomputePendingEditCount(projectId);
        this.notifyStateChanged();

        this.canCancel.set(true);
        this.canReapply.set(false);
    }

    private applyEditEventToState(projectId: string, editEvent: EditEvent, editState: EditState): EditState {
        return editEvent.actions.reduce((accState: EditState, editEventAction: EditEventAction) => {
            const accessKey = editEventAction.layerId ?? editEventAction.tableName;

            const layerSnapshot = accState[accessKey] ?? {};
            const existingEntity = layerSnapshot[editEventAction.entityId];

            let eventProperties = editEventAction.after;
            if (!Object.keys(editEventAction.after ?? {}).length) {
                eventProperties = editEventAction.before;
            }

            if (!existingEntity) {
                return {
                    ...accState,
                    [accessKey]: {
                        ...layerSnapshot,
                        [editEventAction.entityId]: {
                            action: editEventAction.action,
                            businessIdentifier: editEventAction.businessIdentifier,
                            properties: { ...eventProperties },
                            saveOrder: editEventAction.saveOrder,
                            tableName: editEventAction.tableName,
                        },
                    },
                };
            }

            if (editEventAction.action === EditAction.DELETE) {
                return this.buildDeleteEventState(projectId, editEventAction, accState, layerSnapshot, existingEntity);
            }

            return {
                ...accState,
                [accessKey]: {
                    ...layerSnapshot,
                    [editEventAction.entityId]: {
                        ...existingEntity,
                        properties: { ...existingEntity.properties, ...eventProperties },
                        saveOrder: existingEntity.saveOrder ?? editEventAction.saveOrder,
                    },
                },
            };
        }, editState);
    }

    private buildDeleteBeforeSnapshot(projectId: string, candidate: DeletionCandidate): EntityProperties {
        const entityState = this.getEntityState(projectId, candidate.layerId, candidate.entityId);

        if (entityState?.properties && Object.keys(entityState.properties).length) {
            return entityState.properties;
        }

        const beforeFromHistory = this.buildBefore(projectId, candidate.entityId);

        if (beforeFromHistory && Object.keys(beforeFromHistory).length) {
            return beforeFromHistory;
        }

        return candidate.properties;
    }

    private buildDeleteEventState(
        projectId: string,
        editEvent: EditEventAction,
        editState: EditState,
        layerSnapshot: LayerSnapshot,
        entity: EntityEdit,
    ): EditState {
        const storedEvents = this.getEvents(projectId);
        const accessKey = editEvent.layerId ?? editEvent.tableName;

        const isCreatedEntity = storedEvents.some((event: StoredEvent) => {
            return event.actions.some((eventAction: EditEventAction) => {
                return (
                    editEvent.entityId === eventAction.entityId &&
                    !event.canceled &&
                    eventAction.action === EditAction.CREATE
                );
            });
        });

        if (isCreatedEntity) {
            const stateCopy = {
                ...editState,
            };
            delete stateCopy[accessKey][editEvent.entityId];

            return stateCopy;
        }

        return {
            ...editState,
            [accessKey]: {
                ...layerSnapshot,
                [editEvent.entityId]: {
                    ...entity,
                    action: EditAction.DELETE,
                    properties: {},
                    saveOrder: editEvent.saveOrder ?? entity.saveOrder,
                },
            },
        };
    }

    private buildStorageSaturationDecision(context: StorageSaturationContext): StorageSaturationResult {
        const { newEventsJson, nextEventsJson, nextStateJson, projectId, updatedState } = context;

        const quotaBytes = EditModeStateService.defaultEstimatedLocalStorageQuotaBytes;

        const eventsStorageKey = this.getEditLocalStorageKey(projectId, 'events');
        const stateStorageKey = this.getEditLocalStorageKey(projectId, 'state');

        const currentEventsJson = localStorage.getItem(eventsStorageKey);
        const currentStateJson = localStorage.getItem(stateStorageKey);
        const currentUsedBytes = this.getUsedStorageBytes();

        const currentProjectBytes =
            this.calculateStorageEntrySize(eventsStorageKey, currentEventsJson) +
            this.calculateStorageEntrySize(stateStorageKey, currentStateJson);

        const newProjectBytes =
            this.calculateStorageEntrySize(eventsStorageKey, nextEventsJson) +
            this.calculateStorageEntrySize(stateStorageKey, nextStateJson);

        const projectedUsedBytes = currentUsedBytes - currentProjectBytes + newProjectBytes;
        const projectedStorageUsage = this.createStorageUsage(quotaBytes, projectedUsedBytes);

        if (projectedUsedBytes <= quotaBytes) {
            return {
                decision: StorageSaturationDecision.NONE,
                storageUsage: projectedStorageUsage,
            };
        }

        const compactedStateJson = JSON.stringify(this.mergeStateWithBeforeProperties(projectId, updatedState));

        const projectedCompactedBytes =
            currentUsedBytes -
            currentProjectBytes +
            this.calculateStorageEntrySize(eventsStorageKey, newEventsJson) +
            this.calculateStorageEntrySize(stateStorageKey, compactedStateJson);

        if (projectedCompactedBytes <= quotaBytes) {
            return {
                decision: StorageSaturationDecision.CLEAR_HISTORY,
                storageUsage: projectedStorageUsage,
            };
        }

        return {
            decision: StorageSaturationDecision.SAVE_REQUIRED,
            storageUsage: projectedStorageUsage,
        };
    }

    private calculateStorageEntrySize(key: string, value: string | null): number {
        if (!value) {
            return 0;
        }

        // In JavaScript, strings are encoded in UTF-16. In UTF-16, each character is stored using 2 bytes (for the vast majority of common characters).
        return (key.length + value.length) * 2;
    }

    private clearLayerEvents(projectId: string, layerName: string): void {
        const eventStorageKey = this.getEditLocalStorageKey(projectId, 'events');
        const eventsStringified = localStorage.getItem(eventStorageKey);

        if (!eventsStringified?.length) {
            return;
        }

        const events: StoredEvent[] = JSON.parse(eventsStringified);
        const filteredEvents = events
            .map((event: StoredEvent) => {
                return {
                    ...event,
                    actions: event.actions.filter(
                        (eventAction: EditEventAction) =>
                            eventAction.layerId !== layerName && eventAction.tableName !== layerName,
                    ),
                };
            })
            .filter((event: StoredEvent) => !!event.actions.length);
        localStorage.setItem(eventStorageKey, JSON.stringify(filteredEvents));
    }

    private clearLayerState(projectId: string, layerName: string): void {
        const stateStorageKey = this.getEditLocalStorageKey(projectId, 'state');
        const stateStringified = localStorage.getItem(stateStorageKey);

        if (!stateStringified?.length) {
            return;
        }

        const state: EditState = JSON.parse(stateStringified);
        delete state[layerName];

        localStorage.setItem(stateStorageKey, JSON.stringify(state));
    }

    private deleteEntityFromState(state: EditState, eventToCancel: EditEventAction, projectId: string): void {
        const accessKey = eventToCancel.layerId ?? eventToCancel.tableName;

        delete state[accessKey][eventToCancel.entityId];
        if (!Object.keys(state[accessKey]).length) {
            delete state[accessKey];
        }
        localStorage.setItem(this.getEditLocalStorageKey(projectId, 'state'), JSON.stringify(state));

        void this.updateEditSessionLogState(projectId, state);
    }

    private formatEntityState(entityId: string, entity: EntityEdit): EntityState {
        return {
            action: entity.action,
            businessIdentifier: entity.businessIdentifier,
            depositId: entity.properties?.['deposit_id'],
            entityId,
            properties: entity.properties,
            ...(entity.saveOrder !== undefined && { saveOrder: entity.saveOrder }),
            tableName: entity.tableName,
        };
    }

    private getBeforePropertiesFromHistory(projectId: string, entityId: string): EntityProperties {
        const stateBeforeProperties = this.getBeforePropertiesFromState(projectId, entityId);

        if (stateBeforeProperties && Object.keys(stateBeforeProperties).length) {
            return stateBeforeProperties;
        }

        const events = this.getEvents(projectId);
        const storedEntityEvents = this.getEntityEventActions(events, entityId);

        return storedEntityEvents.reduce((acc: Record<string, unknown>, current: EditEventAction) => {
            if (!current.before) {
                return acc;
            }

            Object.entries(current.before).forEach(([key, value]) => {
                if (acc[key]) {
                    return;
                }

                acc[key] = value;
            });

            return acc;
        }, {});
    }

    private getBeforePropertiesFromState(projectId: string, entityId: string): EntityProperties | undefined {
        const state = this.getState(projectId);

        for (const layerSnapshot of Object.values(state)) {
            const beforeProperties = layerSnapshot?.[entityId]?.beforeProperties;
            if (beforeProperties) {
                return beforeProperties;
            }
        }

        return undefined;
    }

    private getEntityEventActions(events: StoredEvent[], entityId: string): EditEventAction[] {
        return events
            .filter((event: StoredEvent) => {
                return (
                    !event.canceled &&
                    event.actions.some((eventAction: EditEventAction) => eventAction.entityId === entityId)
                );
            })
            .flatMap((event: StoredEvent) => {
                return event.actions.filter((eventAction: EditEventAction) => eventAction.entityId === entityId);
            });
    }

    private getEvents(projectId: string): StoredEvent[] {
        const localStorageEditHistoryKey = this.getEditLocalStorageKey(projectId, 'events');

        const historyJson = localStorage.getItem(localStorageEditHistoryKey);
        if (!historyJson) {
            return [];
        }

        return JSON.parse(historyJson);
    }

    private getUsedStorageBytes(): number {
        const prefix = this.getEditLocalStoragePrefix();
        let usedBytes = 0;

        for (let index = 0; index < localStorage.length; index++) {
            const key = localStorage.key(index);

            if (!key?.startsWith(prefix)) {
                continue;
            }

            const value = localStorage.getItem(key) ?? '';

            usedBytes += (key.length + value.length) * 2;
        }

        return usedBytes;
    }

    private initCanCancelAndReapply(projectId: string): void {
        const events = this.getEvents(projectId);
        const canCancel = events.some((event: StoredEvent) => !event.canceled);
        const canReapply = events.some((event: StoredEvent) => event.canceled);

        this.canCancel.set(canCancel);

        this.canReapply.set(canReapply);
    }

    private mergeStateWithBeforeProperties(projectId: string, state: EditState): EditState {
        return Object.entries(state).reduce((compactedState: EditState, [layerId, layerSnapshot]) => {
            compactedState[layerId] = Object.entries(layerSnapshot).reduce(
                (compactedLayerSnapshot: LayerSnapshot, [entityId, entity]) => {
                    compactedLayerSnapshot[entityId] = {
                        ...entity,
                        beforeProperties:
                            entity.beforeProperties ?? this.getBeforePropertiesFromHistory(projectId, entityId),
                    };

                    return compactedLayerSnapshot;
                },
                {},
            );

            return compactedState;
        }, {});
    }

    private notifyStateChanged(): void {
        this.stateTick.update((value) => value + 1);
    }

    private onStorageChange(storageEvent: StorageEvent): void {
        const projectId = this.projectBloc.value()?.id;
        if (!projectId) {
            return;
        }

        const storageKey = storageEvent.key;
        const projectEditPrefix = this.getEditLocalStorageKey(projectId, '');

        const isRelevantEditKey =
            !!storageKey &&
            storageKey.startsWith(projectEditPrefix) &&
            (storageKey.endsWith('/events') || storageKey.endsWith('/state'));

        if (!isRelevantEditKey) {
            return;
        }

        this.refreshPendingEditsState(projectId);
        this.initCanCancelAndReapply(projectId);
    }

    private openStorageSaturationModal(
        projectId: string,
        editEvents: EditEvent[],
        storageSaturationResult: StorageSaturationResult,
    ): void {
        this.editModeStorageSaturationModalService.openModal({
            canClearHistory: storageSaturationResult.decision === StorageSaturationDecision.CLEAR_HISTORY,
            onClearHistory: () => {
                this.clearEditModeHistory(projectId);
                this.addEditsInternal(projectId, editEvents);
            },
            storageUsage: storageSaturationResult.storageUsage,
        });
    }

    private persistEditModeStorage(context: EditModeStoragePersistenceContext): boolean {
        const { eventsStorageKey, nextEventsJson, nextStateJson, stateStorageKey } = context;
        const currentEventsJson = localStorage.getItem(eventsStorageKey);
        const currentStateJson = localStorage.getItem(stateStorageKey);

        try {
            localStorage.setItem(eventsStorageKey, nextEventsJson);
            localStorage.setItem(stateStorageKey, nextStateJson);

            return true;
        } catch (error: unknown) {
            if (currentEventsJson) {
                localStorage.setItem(eventsStorageKey, currentEventsJson);
            } else {
                localStorage.removeItem(eventsStorageKey);
            }

            if (currentStateJson) {
                localStorage.setItem(stateStorageKey, currentStateJson);
            } else {
                localStorage.removeItem(stateStorageKey);
            }

            if (error instanceof DOMException && error.name === 'QuotaExceededError') {
                return false;
            }

            throw error;
        }
    }

    private recomputePendingEditCount(projectId: string): void {
        const state = this.getState(projectId);

        const aggregatedPendingEdits = Object.entries(state).reduce(
            (result, [layerId, layerSnapshot]) => {
                const count = layerSnapshot ? Object.keys(layerSnapshot).length : 0;

                result.total += count;
                result.countByLayer[layerId] = count;
                result.hasEditsByLayer[layerId] = count > 0;

                return result;
            },
            {
                countByLayer: {} as Record<string, number>,
                hasEditsByLayer: {} as Record<string, boolean>,
                total: 0,
            },
        );

        this.pendingEditCount.set(aggregatedPendingEdits.total);
        this.pendingEditCountByLayer.set(aggregatedPendingEdits.countByLayer);
        this.pendingEditsByLayer.set(aggregatedPendingEdits.hasEditsByLayer);
    }

    private refreshPendingEditsState(projectId: string): void {
        this.recomputePendingEditCount(projectId);
        this.notifyStateChanged();
    }

    private updateEntityStateOnCancel(
        projectId: string,
        eventToCancel: EditEventAction,
        otherEntityEvents: EditEventAction[],
        state: EditState,
    ): void {
        const accessKey = eventToCancel.layerId ?? eventToCancel.tableName;

        let entityState = state[accessKey][eventToCancel.entityId];
        entityState = {
            ...entityState,
            action: otherEntityEvents[0].action,
            properties: {
                ...(entityState?.properties ?? {}),
                ...eventToCancel.before,
            },
        };
        state[accessKey][eventToCancel.entityId] = entityState;
        localStorage.setItem(this.getEditLocalStorageKey(projectId, 'state'), JSON.stringify(state));

        void this.updateEditSessionLogState(projectId, state);
    }

    private updateState(projectId: string, editEvent: EditEvent): void {
        const localStorageKey = this.getEditLocalStorageKey(projectId, 'state');

        const state = this.applyEditEventToState(projectId, editEvent, this.getState(projectId));
        localStorage.setItem(localStorageKey, JSON.stringify(state));

        void this.updateEditSessionLogState(projectId, state);
    }

    private updateStateOnCancel(
        projectId: string,
        state: EditState,
        events: StoredEvent[],
        eventToCancel: StoredEvent,
    ): void {
        eventToCancel.actions.forEach((actionToCancel: EditEventAction) => {
            const otherEventsForSameEntity = this.getEntityEventActions(events, actionToCancel.entityId);

            switch (actionToCancel.action) {
                case EditAction.CREATE:
                    this.deleteEntityFromState(state, actionToCancel, projectId);
                    break;
                case EditAction.DELETE:
                case EditAction.UPDATE:
                    if (!otherEventsForSameEntity.length) {
                        this.deleteEntityFromState(state, actionToCancel, projectId);

                        return;
                    }

                    this.updateEntityStateOnCancel(projectId, actionToCancel, otherEventsForSameEntity, state);
                    break;
            }
        });
    }
}
